import { useState, useEffect } from 'react';
import { UserPlus, Search, Filter, Plus, Eye, Pencil, Trash2, X, Star, Tag, ExternalLink, Calendar, Building2 } from 'lucide-react';
import { Dialog } from '@headlessui/react';
import { RecruitmentInfluencer, Collab, recruitmentRef, collabsRef, createDocument, getDocuments, updateDocument, deleteDocument } from '../lib/firebase';

const STATUSES = [
  { value: 'spotted', label: 'Repéré', color: 'bg-blue-500/20 text-blue-300 border-blue-500/20' },
  { value: 'contacted', label: 'Contacté', color: 'bg-yellow-500/20 text-yellow-300 border-yellow-500/20' },
  { value: 'validated', label: 'Validé', color: 'bg-green-500/20 text-green-300 border-green-500/20' },
  { value: 'refused', label: 'Refusé', color: 'bg-red-500/20 text-red-300 border-red-500/20' },
] as const;

const COMMON_TAGS = [
  'beauté', 'sport', 'premium', 'lifestyle', 'mode', 'tech', 'gaming', 
  'food', 'travel', 'fitness', 'micro-influenceur', 'macro-influenceur',
  'nano-influenceur', 'célébrité', 'famille', 'parentalité', 'diy',
  'art', 'musique', 'dance', 'comedy', 'education'
];

export default function Recruitment() {
  const [influencers, setInfluencers] = useState<RecruitmentInfluencer[]>([]);
  const [campaigns, setCampaigns] = useState<Collab[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [selectedInfluencer, setSelectedInfluencer] = useState<RecruitmentInfluencer | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [ratingFilter, setRatingFilter] = useState<string>('all');
  const [tagFilter, setTagFilter] = useState<string>('all');
  const [currentTag, setCurrentTag] = useState('');
  const [formData, setFormData] = useState<Partial<RecruitmentInfluencer>>({
    fullName: '',
    username: '',
    instagramUrl: '',
    tiktokUrl: '',
    youtubeUrl: '',
    internalRating: 3,
    tags: [],
    collaboratedBrands: '',
    status: 'spotted',
    campaignId: '',
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setIsLoading(true);
      const [influencersData, campaignsData] = await Promise.all([
        getDocuments<RecruitmentInfluencer>(recruitmentRef),
        getDocuments<Collab>(collabsRef),
      ]);
      setInfluencers(influencersData);
      setCampaigns(campaignsData);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const influencerData = {
        ...formData,
        updatedAt: new Date(),
      };

      if (selectedInfluencer?.id) {
        await updateDocument(recruitmentRef, selectedInfluencer.id, influencerData);
      } else {
        await createDocument(recruitmentRef, {
          ...influencerData,
          createdAt: new Date(),
        } as RecruitmentInfluencer);
      }

      await loadData();
      handleCloseModal();
    } catch (error) {
      console.error('Error saving influencer:', error);
    }
  };

  const handleDelete = async (influencerId: string) => {
    try {
      await deleteDocument(recruitmentRef, influencerId);
      await loadData();
    } catch (error) {
      console.error('Error deleting influencer:', error);
    }
  };

  const handleEdit = (influencer: RecruitmentInfluencer) => {
    setSelectedInfluencer(influencer);
    setFormData({
      fullName: influencer.fullName,
      username: influencer.username,
      instagramUrl: influencer.instagramUrl || '',
      tiktokUrl: influencer.tiktokUrl || '',
      youtubeUrl: influencer.youtubeUrl || '',
      internalRating: influencer.internalRating,
      tags: [...influencer.tags],
      collaboratedBrands: influencer.collaboratedBrands,
      status: influencer.status,
      campaignId: influencer.campaignId || '',
    });
    setIsModalOpen(true);
  };

  const handleViewDetails = (influencer: RecruitmentInfluencer) => {
    setSelectedInfluencer(influencer);
    setIsDetailModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedInfluencer(null);
    setFormData({
      fullName: '',
      username: '',
      instagramUrl: '',
      tiktokUrl: '',
      youtubeUrl: '',
      internalRating: 3,
      tags: [],
      collaboratedBrands: '',
      status: 'spotted',
      campaignId: '',
    });
  };

  const handleAddTag = () => {
    if (currentTag.trim() && !formData.tags?.includes(currentTag.trim())) {
      setFormData(prev => ({
        ...prev,
        tags: [...(prev.tags || []), currentTag.trim()]
      }));
      setCurrentTag('');
    }
  };

  const handleRemoveTag = (tagToRemove: string) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags?.filter(tag => tag !== tagToRemove) || []
    }));
  };

  const renderStars = (rating: number, interactive = false, onChange?: (rating: number) => void) => {
    return (
      <div className="flex gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            type={interactive ? "button" : undefined}
            onClick={interactive && onChange ? () => onChange(star) : undefined}
            className={`${interactive ? 'cursor-pointer hover:scale-110' : 'cursor-default'} transition-transform`}
          >
            <Star
              className={`w-4 h-4 ${
                star <= rating
                  ? 'text-yellow-400 fill-yellow-400'
                  : 'text-gray-400'
              }`}
            />
          </button>
        ))}
      </div>
    );
  };

  const formatDate = (date: Date | { seconds: number } | undefined) => {
    if (!date) return 'Date inconnue';
    
    if ('seconds' in date) {
      return new Date(date.seconds * 1000).toLocaleDateString('fr-FR', {
        day: 'numeric',
        month: 'long',
        year: 'numeric'
      });
    }
    
    return new Date(date).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const getStatusBadge = (status: string) => {
    const statusConfig = STATUSES.find(s => s.value === status);
    return statusConfig || STATUSES[0];
  };

  const filteredInfluencers = influencers.filter(influencer => {
    const matchesSearch = 
      influencer.fullName.toLowerCase().includes(search.toLowerCase()) ||
      influencer.username.toLowerCase().includes(search.toLowerCase()) ||
      influencer.tags.some(tag => tag.toLowerCase().includes(search.toLowerCase()));
    
    const matchesStatus = statusFilter === 'all' || influencer.status === statusFilter;
    const matchesRating = ratingFilter === 'all' || influencer.internalRating.toString() === ratingFilter;
    const matchesTag = tagFilter === 'all' || influencer.tags.includes(tagFilter);

    return matchesSearch && matchesStatus && matchesRating && matchesTag;
  });

  const allTags = [...new Set(influencers.flatMap(inf => inf.tags))].sort();

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-accent-purple to-accent-blue flex items-center justify-center">
            <UserPlus className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-white">Recrutement</h1>
            <p className="text-gray-400">Gérez vos influenceurs repérés pour les futures campagnes</p>
          </div>
        </div>
        <button
          onClick={() => setIsModalOpen(true)}
          className="flex items-center gap-2 px-4 py-2 bg-accent-blue text-white rounded-lg hover:bg-accent-blue/90 transition-colors"
        >
          <Plus className="w-5 h-5" />
          Ajouter un influenceur
        </button>
      </div>

      <div className="bg-dark-50 rounded-xl border border-dark-200 p-6">
        <div className="flex flex-col lg:flex-row gap-4 mb-6">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Rechercher par nom, pseudo ou tag..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-10"
              />
            </div>
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="w-full lg:w-48"
          >
            <option value="all">Tous les statuts</option>
            {STATUSES.map((status) => (
              <option key={status.value} value={status.value}>{status.label}</option>
            ))}
          </select>
          <select
            value={ratingFilter}
            onChange={(e) => setRatingFilter(e.target.value)}
            className="w-full lg:w-48"
          >
            <option value="all">Toutes les notes</option>
            {[5, 4, 3, 2, 1].map((rating) => (
              <option key={rating} value={rating.toString()}>{rating} étoile{rating > 1 ? 's' : ''}</option>
            ))}
          </select>
          <select
            value={tagFilter}
            onChange={(e) => setTagFilter(e.target.value)}
            className="w-full lg:w-48"
          >
            <option value="all">Tous les tags</option>
            {allTags.map((tag) => (
              <option key={tag} value={tag}>{tag}</option>
            ))}
          </select>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent-blue"></div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredInfluencers.map((influencer) => (
              <div key={influencer.id} className="bg-dark-100 rounded-lg p-4 hover:bg-dark-100/80 transition-colors">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1 min-w-0">
                    <h3 className="text-white font-medium truncate">{influencer.fullName}</h3>
                    <p className="text-gray-400 text-sm">@{influencer.username}</p>
                  </div>
                  <div className="flex items-center gap-2 ml-2">
                    <button
                      onClick={() => handleViewDetails(influencer)}
                      className="p-1 text-gray-400 hover:text-white rounded-lg hover:bg-dark-200"
                      title="Voir les détails"
                    >
                      <Eye className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleEdit(influencer)}
                      className="p-1 text-gray-400 hover:text-white rounded-lg hover:bg-dark-200"
                      title="Modifier"
                    >
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(influencer.id!)}
                      className="p-1 text-red-500 hover:text-red-400 rounded-lg hover:bg-dark-200"
                      title="Supprimer"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${getStatusBadge(influencer.status).color}`}>
                      {getStatusBadge(influencer.status).label}
                    </span>
                    {renderStars(influencer.internalRating)}
                  </div>

                  {influencer.tags.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {influencer.tags.slice(0, 3).map((tag, index) => (
                        <span key={index} className="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-accent-blue/20 text-accent-blue">
                          {tag}
                        </span>
                      ))}
                      {influencer.tags.length > 3 && (
                        <span className="text-xs text-gray-400">+{influencer.tags.length - 3}</span>
                      )}
                    </div>
                  )}

                  <div className="flex items-center gap-2">
                    {influencer.instagramUrl && (
                      <a
                        href={influencer.instagramUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-1 text-pink-500 hover:text-pink-400 rounded-lg hover:bg-dark-200"
                        title="Instagram"
                      >
                        <ExternalLink className="w-4 h-4" />
                      </a>
                    )}
                    {influencer.tiktokUrl && (
                      <a
                        href={influencer.tiktokUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-1 text-purple-500 hover:text-purple-400 rounded-lg hover:bg-dark-200"
                        title="TikTok"
                      >
                        <ExternalLink className="w-4 h-4" />
                      </a>
                    )}
                    {influencer.youtubeUrl && (
                      <a
                        href={influencer.youtubeUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-1 text-red-500 hover:text-red-400 rounded-lg hover:bg-dark-200"
                        title="YouTube"
                      >
                        <ExternalLink className="w-4 h-4" />
                      </a>
                    )}
                  </div>

                  <div className="text-xs text-gray-400">
                    Ajouté le {formatDate(influencer.createdAt)}
                  </div>
                </div>
              </div>
            ))}

            {filteredInfluencers.length === 0 && (
              <div className="col-span-full text-center py-12">
                <div className="w-16 h-16 rounded-full bg-dark-200 flex items-center justify-center mx-auto mb-4">
                  <UserPlus className="w-8 h-8 text-gray-400" />
                </div>
                <h3 className="text-lg font-medium text-white mb-2">Aucun influenceur trouvé</h3>
                <p className="text-gray-400">
                  {search || statusFilter !== 'all' || ratingFilter !== 'all' || tagFilter !== 'all'
                    ? 'Essayez de modifier vos filtres de recherche'
                    : 'Commencez par ajouter votre premier influenceur repéré'}
                </p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Add/Edit Modal */}
      <Dialog
        open={isModalOpen}
        onClose={handleCloseModal}
        className="fixed inset-0 z-10 overflow-y-auto"
      >
        <div className="flex items-center justify-center min-h-screen p-4">
          <Dialog.Overlay className="fixed inset-0 bg-black bg-opacity-30" />
          <div className="relative bg-dark-50 rounded-xl max-w-2xl w-full mx-4 p-6 border border-dark-200">
            <div className="absolute top-4 right-4">
              <button
                onClick={handleCloseModal}
                className="text-gray-400 hover:text-gray-300"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <h2 className="text-2xl font-bold text-white mb-6">
                  {selectedInfluencer ? 'Modifier l\'influenceur' : 'Ajouter un influenceur'}
                </h2>

                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-200">
                        Nom complet
                      </label>
                      <input
                        type="text"
                        value={formData.fullName}
                        onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                        className="mt-1 block w-full"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-200">
                        @Pseudo
                      </label>
                      <input
                        type="text"
                        value={formData.username}
                        onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                        className="mt-1 block w-full"
                        placeholder="sans le @"
                        required
                      />
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-200">
                        Lien Instagram (optionnel)
                      </label>
                      <input
                        type="url"
                        value={formData.instagramUrl}
                        onChange={(e) => setFormData({ ...formData, instagramUrl: e.target.value })}
                        className="mt-1 block w-full"
                        placeholder="https://instagram.com/..."
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-200">
                        Lien TikTok (optionnel)
                      </label>
                      <input
                        type="url"
                        value={formData.tiktokUrl}
                        onChange={(e) => setFormData({ ...formData, tiktokUrl: e.target.value })}
                        className="mt-1 block w-full"
                        placeholder="https://tiktok.com/@..."
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-200">
                        Lien YouTube (optionnel)
                      </label>
                      <input
                        type="url"
                        value={formData.youtubeUrl}
                        onChange={(e) => setFormData({ ...formData, youtubeUrl: e.target.value })}
                        className="mt-1 block w-full"
                        placeholder="https://youtube.com/..."
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-200 mb-2">
                        Note interne
                      </label>
                      {renderStars(formData.internalRating || 3, true, (rating) => 
                        setFormData({ ...formData, internalRating: rating })
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-200">
                        Statut
                      </label>
                      <select
                        value={formData.status}
                        onChange={(e) => setFormData({ ...formData, status: e.target.value as RecruitmentInfluencer['status'] })}
                        className="mt-1 block w-full"
                      >
                        {STATUSES.map((status) => (
                          <option key={status.value} value={status.value}>{status.label}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-200 mb-2">
                      Tags
                    </label>
                    <div className="flex flex-wrap gap-2 mb-3">
                      {formData.tags?.map((tag, index) => (
                        <span
                          key={index}
                          className="inline-flex items-center gap-1 px-2 py-1 bg-accent-blue/20 text-accent-blue rounded-full text-sm"
                        >
                          {tag}
                          <button
                            type="button"
                            onClick={() => handleRemoveTag(tag)}
                            className="hover:text-accent-blue/80"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </span>
                      ))}
                    </div>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={currentTag}
                        onChange={(e) => setCurrentTag(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            handleAddTag();
                          }
                        }}
                        className="flex-1"
                        placeholder="Ajouter un tag..."
                      />
                      <button
                        type="button"
                        onClick={handleAddTag}
                        className="px-3 py-2 bg-accent-blue text-white rounded-lg hover:bg-accent-blue/90"
                      >
                        <Plus className="w-4 h-4" />
                      </button>
                    </div>
                    <div className="mt-2 flex flex-wrap gap-1">
                      {COMMON_TAGS.filter(tag => !formData.tags?.includes(tag)).map((tag) => (
                        <button
                          key={tag}
                          type="button"
                          onClick={() => {
                            setFormData(prev => ({
                              ...prev,
                              tags: [...(prev.tags || []), tag]
                            }));
                          }}
                          className="text-xs px-2 py-1 bg-dark-200 text-gray-400 rounded hover:bg-dark-300 hover:text-white"
                        >
                          {tag}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-200">
                      Marques déjà collaborées
                    </label>
                    <textarea
                      value={formData.collaboratedBrands}
                      onChange={(e) => setFormData({ ...formData, collaboratedBrands: e.target.value })}
                      rows={3}
                      className="mt-1 block w-full"
                      placeholder="Ex: Nike, Adidas, L'Oréal..."
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-200">
                      Ajouter à une campagne (optionnel)
                    </label>
                    <select
                      value={formData.campaignId}
                      onChange={(e) => setFormData({ ...formData, campaignId: e.target.value })}
                      className="mt-1 block w-full"
                    >
                      <option value="">Aucune campagne</option>
                      {campaigns.map((campaign) => (
                        <option key={campaign.id} value={campaign.id}>
                          {campaign.name} - {campaign.brand}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="flex justify-end gap-3 mt-6">
                  <button
                    type="button"
                    onClick={handleCloseModal}
                    className="px-4 py-2 text-sm font-medium text-gray-300 bg-dark-200 border border-dark-300 rounded-lg hover:bg-dark-300"
                  >
                    Annuler
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 text-sm font-medium text-white bg-accent-blue border border-transparent rounded-lg hover:bg-accent-blue/90"
                  >
                    {selectedInfluencer ? 'Modifier' : 'Ajouter'}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      </Dialog>

      {/* Detail Modal */}
      <Dialog
        open={isDetailModalOpen}
        onClose={() => setIsDetailModalOpen(false)}
        className="fixed inset-0 z-10 overflow-y-auto"
      >
        <div className="flex items-center justify-center min-h-screen p-4">
          <Dialog.Overlay className="fixed inset-0 bg-black bg-opacity-30" />
          <div className="relative bg-dark-50 rounded-xl max-w-2xl w-full mx-4 p-6 border border-dark-200">
            {selectedInfluencer && (
              <>
                <div className="absolute top-4 right-4">
                  <button
                    onClick={() => setIsDetailModalOpen(false)}
                    className="text-gray-400 hover:text-gray-300"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>

                <div className="space-y-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <h2 className="text-2xl font-bold text-white">{selectedInfluencer.fullName}</h2>
                      <p className="text-gray-400">@{selectedInfluencer.username}</p>
                    </div>
                    <button
                      onClick={() => {
                        setIsDetailModalOpen(false);
                        handleEdit(selectedInfluencer);
                      }}
                      className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-accent-blue rounded-lg hover:bg-accent-blue/90"
                    >
                      <Pencil className="w-4 h-4" />
                      Modifier
                    </button>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-4">
                      <div className="bg-dark-100 rounded-lg p-4">
                        <h3 className="text-lg font-medium text-white mb-3">Informations</h3>
                        <div className="space-y-3">
                          <div className="flex items-center justify-between">
                            <span className="text-gray-400">Statut</span>
                            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${getStatusBadge(selectedInfluencer.status).color}`}>
                              {getStatusBadge(selectedInfluencer.status).label}
                            </span>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-gray-400">Note interne</span>
                            {renderStars(selectedInfluencer.internalRating)}
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-gray-400">Date d'ajout</span>
                            <span className="text-white">{formatDate(selectedInfluencer.createdAt)}</span>
                          </div>
                        </div>
                      </div>

                      <div className="bg-dark-100 rounded-lg p-4">
                        <h3 className="text-lg font-medium text-white mb-3">Liens sociaux</h3>
                        <div className="space-y-2">
                          {selectedInfluencer.instagramUrl && (
                            <a
                              href={selectedInfluencer.instagramUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center gap-2 text-pink-500 hover:text-pink-400"
                            >
                              <ExternalLink className="w-4 h-4" />
                              Instagram
                            </a>
                          )}
                          {selectedInfluencer.tiktokUrl && (
                            <a
                              href={selectedInfluencer.tiktokUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center gap-2 text-purple-500 hover:text-purple-400"
                            >
                              <ExternalLink className="w-4 h-4" />
                              TikTok
                            </a>
                          )}
                          {selectedInfluencer.youtubeUrl && (
                            <a
                              href={selectedInfluencer.youtubeUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center gap-2 text-red-500 hover:text-red-400"
                            >
                              <ExternalLink className="w-4 h-4" />
                              YouTube
                            </a>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="space-y-4">
                      <div className="bg-dark-100 rounded-lg p-4">
                        <h3 className="text-lg font-medium text-white mb-3">Tags</h3>
                        <div className="flex flex-wrap gap-2">
                          {selectedInfluencer.tags.map((tag, index) => (
                            <span
                              key={index}
                              className="inline-flex items-center px-2 py-1 bg-accent-blue/20 text-accent-blue rounded-full text-sm"
                            >
                              {tag}
                            </span>
                          ))}
                        </div>
                      </div>

                      {selectedInfluencer.collaboratedBrands && (
                        <div className="bg-dark-100 rounded-lg p-4">
                          <h3 className="text-lg font-medium text-white mb-3">Marques collaborées</h3>
                          <p className="text-gray-400 whitespace-pre-wrap">{selectedInfluencer.collaboratedBrands}</p>
                        </div>
                      )}

                      {selectedInfluencer.campaignId && (
                        <div className="bg-dark-100 rounded-lg p-4">
                          <h3 className="text-lg font-medium text-white mb-3">Campagne associée</h3>
                          <div className="flex items-center gap-2">
                            <Building2 className="w-4 h-4 text-accent-blue" />
                            <span className="text-white">
                              {campaigns.find(c => c.id === selectedInfluencer.campaignId)?.name || 'Campagne inconnue'}
                            </span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </Dialog>
    </div>
  );
}